// Home Improvement Tasks Form - Bulk Entry
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Hammer, Plus, X } from 'lucide-react';

interface HomeImprovementFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
}

interface ProjectItem {
  id: string;
  taskName: string;
  notes: string;
}

export default function HomeImprovementForm({ onAdd }: HomeImprovementFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<ProjectItem[]>([
    { id: '1', taskName: '', notes: '' }
  ]);

  const handleAddRow = () => {
    if (items.length >= 30) return;
    setItems([...items, { id: Date.now().toString(), taskName: '', notes: '' }]);
  };

  const handleRemoveRow = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, field: 'taskName' | 'notes', value: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validItems = items.filter(item => item.taskName.trim());
    
    if (validItems.length === 0) return;

    validItems.forEach(item => {
      onAdd({
        title: `🏠 ${item.taskName.trim()}`,
        notes: item.notes || undefined,
        metadata: {
          type: 'home_improvement'
        }
      });
    });

    setItems([{ id: Date.now().toString(), taskName: '', notes: '' }]);
    setIsOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <Hammer className="w-5 h-5" />
        Add Home Projects
      </button>

      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Home Improvement Projects"
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-400">
            Add up to 30 projects at once. Fill in project names and optional notes.
          </p>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start">
                <div className="flex-shrink-0 w-8 pt-3 text-sm text-slate-500 font-semibold">
                  {index + 1}.
                </div>

                <div className="flex-1">
                  <input
                    type="text"
                    value={item.taskName}
                    onChange={(e) => handleUpdateItem(item.id, 'taskName', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base mb-2"
                    placeholder="Project name (e.g., Fix leaky faucet)"
                    autoFocus={index === 0}
                  />
                  <input
                    type="text"
                    value={item.notes}
                    onChange={(e) => handleUpdateItem(item.id, 'notes', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                    placeholder="Notes (optional)"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveRow(item.id)}
                  disabled={items.length === 1}
                  className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove project"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {items.length < 30 && (
            <button
              type="button"
              onClick={handleAddRow}
              className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Another Project ({items.length}/30)
            </button>
          )}

          <div className="text-sm text-slate-400 text-center">
            {items.filter(i => i.taskName.trim()).length} project(s) ready to add
          </div>

          <button
            type="submit"
            disabled={items.filter(i => i.taskName.trim()).length === 0}
            className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Hammer className="w-5 h-5" />
            Add {items.filter(i => i.taskName.trim()).length} Project(s)
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
